import { connector as api_companydb } from "@/api/connector";
import { getField, updateField } from 'vuex-map-fields';
import { GetUserAllowedActionsQuery } from "@/modules/advanced_settings/app/GetUserAllowedActionsQuery";
import { query_dispatcher } from "@/api/client";

export const SET_PERMISSION = "SET_PERMISSION";
export const SET_CAN_CREATE_COMPANY = "SET_CAN_CREATE_COMPANY";
export const SET_ALBARAN_ACCESS_LOADING = "SET_ALBARAN_ACCESS_LOADING";
export const SET_ALBARAN_ACCESS = "SET_ALBARAN_ACCESS";
export const SET_NOMINA_ACCESS_LOADING = "SET_NOMINA_ACCESS_LOADING";
export const SET_NOMINA_ACCESS = "SET_NOMINA_ACCESS";

class Permission {
    constructor({ id, role_kind, is_owner, is_readonly, is_writer, is_invited, can_write, general, is_employee, is_customer}) {
        this.id = id;
        this.role_kind = role_kind;
        this.is_owner = is_owner;
        this.is_readonly = is_readonly;
        this.is_writer = is_writer;
        this.is_invited = is_invited;
        this.can_write = can_write;
        this.general = general;
        this.is_employee = is_employee;
        this.is_customer = is_customer;
    }
}

const defaultValues = {
    id: false,
    role_kind: false,
    is_owner: false,
    is_readonly: false,
    is_writer: false,
    is_invited: false,
    can_write: false,
    general: {
        is_organization: false,
        companydb_actividad: false,
        can_admin: false,
        is_customer_consult: false
    },
    is_employee: false,
    is_customer: false
}

const emptyInvoiceApprovalAccess = companyId => ({ companyId: Number(companyId), loaded: false, enabled: false, allowed_actions: [] });

const state = {
    invoiceApprovalAccess: emptyInvoiceApprovalAccess(null),
    permission: new Permission(defaultValues),
    canCreateCompany: false,
    albaranAccess: {
        companyId: null,
        loading: false,
        loaded: false,
        enabled: false,
        allowed_actions: [],
        centers: [],
        primary_center_id: '',
        catalog_read_only: true,
        all_centers: false,
        has_flows: false,
        flow_count: 0,
        works_with_catalog: false,
        works_with_pedidos: false,
        pedidos_read_only: true,
        customer_documents_enabled: false,
    },
    nominaAccess: {
        companyId: null,
        loading: false,
        loaded: false,
        enabled: false,
        allowed_actions: [],
    }
}

const getPermissions = async (context, companydbId) => {
    const response = await api_companydb.getPermissions({ id: companydbId });
    try { await context.dispatch('loadInvoiceApprovalAccess', companydbId); }
    catch (error) { console.error(error); }
    context.commit(SET_PERMISSION, response.data);
    return response;
}

const loadInvoiceApprovalAccess = async (context, companyId) => {
    context.commit('SET_INVOICE_APPROVAL_ACCESS', emptyInvoiceApprovalAccess(companyId));
    try {
        const access = await query_dispatcher.dispatch('invoice_approval.access.get', { company_id: Number(companyId) });
        context.commit('SET_INVOICE_APPROVAL_ACCESS', {
            companyId: Number(companyId), loaded: true, enabled: Boolean(access.enabled),
            allowed_actions: Array.isArray(access.allowed_actions) ? access.allowed_actions : [],
        });
        return access;
    } catch (error) {
        context.commit('SET_INVOICE_APPROVAL_ACCESS', emptyInvoiceApprovalAccess(companyId));
        throw error;
    }
}

// Carga (una vez, tras login) si el usuario puede crear empresas, según el permiso
// del módulo 'invoice'. Deny-by-default: sin config (null) => NO concedido, al revés
// que isActionAllowed (que devuelve true con null).
const loadCanCreateCompany = async (context) => {
    try {
        const allowed = await new GetUserAllowedActionsQuery(query_dispatcher).execute('invoice');
        const can = Array.isArray(allowed) && allowed.includes('invoice_create_company');
        context.commit(SET_CAN_CREATE_COMPANY, can);
    } catch (e) {
        context.commit(SET_CAN_CREATE_COMPANY, false);
    }
}

const loadAlbaranAccess = async (context, companyId) => {
    context.commit(SET_ALBARAN_ACCESS_LOADING, companyId);
    try {
        const access = await query_dispatcher.dispatch('albaran_factura.access.get', {
            company_id: Number(companyId),
        });
        context.commit(SET_ALBARAN_ACCESS, { ...access, companyId: Number(companyId) });
        return access;
    } catch (error) {
        context.commit(SET_ALBARAN_ACCESS, { companyId: Number(companyId) });
        throw error;
    }
}

// Petición en vuelo por empresa, para que dos llamadas simultáneas compartan UNA.
//
// Lo piden dos sitios en cada navegación: el guard del router
// (`resolveNominaAccessRedirect`) y el `created` de cada vista de nóminas. Sin esto
// eran dos `nominas.access.get` seguidas e idénticas — se veía en los logs, en pares.
//
// Vive fuera del state de Vuex a propósito: es una promesa, no datos que la UI deba
// observar, y meterla en el state la haría reactiva sin ninguna ventaja.
const nominaAccessRequestByCompany = new Map();

/**
 * Carga las acciones permitidas del módulo de nóminas para una empresa.
 *
 * Acepta `companyId` suelto o `{ companyId, force }`. Con `force` se relee aunque
 * esté cacheado: hace falta cuando los permisos del usuario acaban de cambiar.
 *
 * Reutiliza lo ya cargado de ESA empresa. El límite es justo ese: el permiso es por
 * empresa, y reutilizar el de otra dejaría al usuario con los botones de una empresa
 * en la que quizá no puede tocar nada.
 */
const loadNominaAccess = async (context, payload) => {
    const isDetailedPayload = payload && typeof payload === 'object';
    const companyId = Number(isDetailedPayload ? payload.companyId : payload);
    const force = Boolean(isDetailedPayload && payload.force);

    const current = context.state.nominaAccess;
    // `failed` importa: la mutación de error también deja `loaded: true` (se invoca
    // sin `allowed_actions`), así que sin distinguirlo un corte de red de un segundo
    // dejaría al usuario sin permisos hasta un F5.
    if (!force && current.loaded && !current.failed && current.companyId === companyId) {
        return current;
    }
    if (!force && nominaAccessRequestByCompany.has(companyId)) {
        // Ya hay una en vuelo para esta empresa: se espera ESA, en vez de lanzar otra.
        return nominaAccessRequestByCompany.get(companyId);
    }

    context.commit(SET_NOMINA_ACCESS_LOADING, companyId);
    const request = (async () => {
        try {
            const access = await query_dispatcher.dispatch('nominas.access.get', {
                company_id: companyId,
            });
            context.commit(SET_NOMINA_ACCESS, { ...access, companyId });
            return access;
        } catch (error) {
            context.commit(SET_NOMINA_ACCESS, { companyId, failed: true });
            throw error;
        } finally {
            // Se suelta SIEMPRE, también al fallar: cachear el error dejaría al
            // usuario sin permisos hasta un F5 por un corte de red de un segundo.
            nominaAccessRequestByCompany.delete(companyId);
        }
    })();
    nominaAccessRequestByCompany.set(companyId, request);
    return request;
}

const actions = {
    loadInvoiceApprovalAccess,
    getPermissions,
    loadCanCreateCompany,
    loadAlbaranAccess,
    loadNominaAccess,
}

const getters = {
    invoiceApprovalAccess: state => state.invoiceApprovalAccess,
    permission: state => state.permission,
    canCreateCompany: state => state.canCreateCompany,
    albaranAccess: state => state.albaranAccess,
    isAlbaranActionAllowed: state => action => (
        state.albaranAccess.loaded
        && state.albaranAccess.allowed_actions.includes(action)
    ),
    nominaAccess: state => state.nominaAccess,
    isNominaActionAllowed: state => action => (
        state.nominaAccess.loaded
        && state.nominaAccess.allowed_actions.includes(action)
    ),
    getField
}

const mutations = {
    SET_INVOICE_APPROVAL_ACCESS(state, access) { state.invoiceApprovalAccess = access; },
    [SET_PERMISSION](state, data) {
        state.permission = new Permission(data);
    },
    [SET_CAN_CREATE_COMPANY](state, value) {
        state.canCreateCompany = value;
    },
    // Deny-by-default while the policy is in flight, but ONLY when the company
    // changes: showing company B's screens with company A's permissions is the
    // thing this guards against. A REFETCH of the company already loaded must
    // keep the resolved policy — otherwise every re-navigation (e.g. the
    // ?tab= query the albaran/factura tabs write, which re-runs the access
    // guard) briefly reads as "all actions revoked", and views that branch on
    // allowed_actions flip themselves back mid-click.
    [SET_ALBARAN_ACCESS_LOADING](state, companyId) {
        const isSameCompany = state.albaranAccess.loaded
            && Number(state.albaranAccess.companyId) === Number(companyId);
        if (isSameCompany) {
            state.albaranAccess = { ...state.albaranAccess, loading: true };
            return;
        }
        state.albaranAccess = {
            companyId: Number(companyId),
            loading: true,
            loaded: false,
            enabled: false,
            allowed_actions: [],
            centers: [],
            primary_center_id: '',
            catalog_read_only: true,
            all_centers: false,
            has_flows: false,
            flow_count: 0,
            works_with_catalog: false,
            works_with_pedidos: false,
            pedidos_read_only: true,
            customer_documents_enabled: false,
        };
    },
    [SET_ALBARAN_ACCESS](state, data) {
        state.albaranAccess = {
            companyId: data.companyId,
            loading: false,
            loaded: true,
            enabled: Boolean(data.enabled),
            role: data.role || '',
            source: data.source || 'system',
            allowed_actions: Array.isArray(data.allowed_actions) ? data.allowed_actions : [],
            centers: Array.isArray(data.centers) ? data.centers : [],
            primary_center_id: String(data.primary_center_id || ''),
            catalog_read_only: data.catalog_read_only !== false,
            all_centers: Boolean(data.all_centers),
            // D14: without a flow the module asks the company to create one first.
            has_flows: Boolean(data.has_flows),
            flow_count: Number(data.flow_count || 0),
            // D9: each screen shows while any flow of the company uses it.
            works_with_catalog: Boolean(data.works_with_catalog),
            works_with_pedidos: Boolean(data.works_with_pedidos),
            pedidos_read_only: data.pedidos_read_only !== false,
            customer_documents_enabled: Boolean(data.customer_documents_enabled),
        };
    },
    [SET_NOMINA_ACCESS_LOADING](state, companyId) {
        state.nominaAccess = {
            companyId: Number(companyId),
            loading: true,
            loaded: false,
            enabled: false,
            allowed_actions: [],
        };
    },
    [SET_NOMINA_ACCESS](state, data) {
        state.nominaAccess = {
            companyId: data.companyId,
            loading: false,
            loaded: true,
            // Marca el intento fallido para que no se cachee como válido: sin esto,
            // un fallo de red dejaba al usuario sin permisos hasta recargar.
            failed: Boolean(data.failed),
            enabled: Boolean(data.enabled),
            role: data.role || '',
            source: data.source || 'system',
            allowed_actions: Array.isArray(data.allowed_actions) ? data.allowed_actions : [],
        };
    },
    updateField,
}

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
  }
